import React, { useEffect, useState } from "react";
import { api, exportCsv, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check } from "@/components/Check";
import { Plus, Download, Upload, Trash2, Settings as Gear, FolderInput, GripVertical, ArrowUpDown } from "lucide-react";
import useResizableColumns from "@/lib/useResizableColumns";
import EditableHeader from "@/components/EditableHeader";
import PipelineConfigModal from "@/components/PipelineConfigModal";
import ImportModal from "@/components/ImportModal";
import LastImportBanner from "@/components/LastImportBanner";
import { useTableSort, sortRows, SortCaret, useManualOrder, applyManualOrder, useRowDrag, SortStatus } from "@/components/TableSort";

const DEAL_DEFAULTS = {
  0: 180, 1: 200, 2: 130, 3: 150, 4: 140, 5: 130,
  6: 130, 7: 100, 8: 130, 9: 90, 10: 130, 11: 130,
  12: 110, 13: 130, 14: 220, 15: 72,
};

const FALLBACK_CONFIG = {
  page_title: "Pipeline",
  subject_label: "Deal",
  property_label: "PROJECT",
  labels: {
    client: "CLIENT", property: "PROJECT", deal_type: "TYPE",
    stage: "STAGE", source: "SOURCE", expected_close: "EXP. CLOSE",
    price: "VALUE", commission_pct: "RATE %", projected_commission: "PROJECTED",
    closed: "CLOSED", closed_date: "CLOSED DATE",
    next_follow_up: "FOLLOW-UP", priority: "PRIORITY",
    status: "STATUS", notes: "NOTES",
  },
  stages: ["Lead", "Qualified", "Proposal", "Negotiation", "Won", "Lost"],
  deal_types: ["New Business", "Renewal", "Upsell", "Other"],
  priorities: ["Low", "Medium", "High"],
};

export default function DealPipeline() {
  const [deals, setDeals] = useState([]);
  const [filter, setFilter] = useState("");
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [configOpen, setConfigOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [trackerTabs, setTrackerTabs] = useState([]);
  const [sendDeal, setSendDeal] = useState(null);
  const [importBump, setImportBump] = useState(0);
  const onImported = async () => { await load(); setImportBump((b) => b + 1); };
  const { user } = useAuth();
  const tableKey = `deals:${user?.id || "anon"}`;
  const { sort, toggle } = useTableSort(tableKey);
  const { order, setOrder, reset: resetOrder } = useManualOrder(tableKey);
  const [manual, setManual] = useState(false);
  const onSort = (f, t) => { setManual(false); toggle(f, t); };
  const { setRef, widthStyle, Grip, reset } = useResizableColumns(
    "deals-v1", 16, { defaults: DEAL_DEFAULTS },
  );

  const loadSettings = async () => {
    try {
      const s = await api.get("/settings").then((r) => r.data);
      if (s?.pipeline_config) {
        setConfig({
          ...FALLBACK_CONFIG,
          ...s.pipeline_config,
          labels: { ...FALLBACK_CONFIG.labels, ...(s.pipeline_config.labels || {}) },
        });
      }
    } catch (_) { /* noop */ }
  };

  const saveConfig = async (newCfg) => {
    setConfig(newCfg);
    try {
      await api.put("/settings", { pipeline_config: newCfg });
    } catch (_) { /* noop */ }
  };

  const saveLabel = (k, newLabel) => {
    saveConfig({ ...config, labels: { ...config.labels, [k]: newLabel } });
  };
  const L = (k) => config.labels?.[k] || FALLBACK_CONFIG.labels[k] || k.toUpperCase();

  const load = async () => {
    const d = await api.get("/deals").then((r) => r.data);
    setDeals(Array.isArray(d) ? d : []);
  };
  const loadTabs = async () => {
    try {
      const t = await api.get("/tabs").then((r) => r.data);
      setTrackerTabs((Array.isArray(t) ? t : []).filter((x) => x.type === "tracker"));
    } catch (_) { /* noop */ }
  };
  useEffect(() => {
    load();
    loadSettings();
    loadTabs();
  }, []);

  const sendToProject = async (tabId) => {
    const d = sendDeal;
    if (!d || !tabId) return;
    await api.post(`/tabs/${tabId}/rows`, {
      client: d.client || "",
      subject: d.property || d.client || "",
      date: d.closed_date || d.expected_close_date || null,
      notes: d.notes || "",
    });
    setSendDeal(null);
  };

  const add = async () => {
    const initialStage = (config.stages && config.stages[0]) || "Lead";
    const d = await api.post("/deals", { client: "", stage: initialStage }).then((r) => r.data);
    setDeals((p) => [d, ...p]);
  };

  const update = async (id, patch) => {
    const cur = deals.find((d) => d.id === id);
    if (!cur) return;
    const next = { ...cur, ...patch };
    setDeals((p) => p.map((d) => (d.id === id ? next : d)));
    const fresh = await api.put(`/deals/${id}`, next).then((r) => r.data);
    setDeals((p) => p.map((d) => (d.id === id ? fresh : d)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    setDeals((p) => p.filter((d) => d.id !== id));
    await api.delete(`/deals/${id}`);
  };

  const onExport = () => exportCsv("deals.csv", deals);

  const filtered = deals.filter((d) => {
    const q = filter.toLowerCase();
    return !q || [d.client, d.property, d.stage, d.source, d.notes]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const filterActive = filter.trim().length > 0;
  const manualActive = manual && !filterActive;
  const sorted = sortRows(filtered, sort, (r, f) => r[f]);
  const display = manualActive ? applyManualOrder(deals, order) : sorted;
  const drag = useRowDrag(manualActive, display.map((d) => d.id), setOrder);
  const labelFor = (f) => L(f === "expected_close_date" ? "expected_close" : f);

  const totalProjected = deals.reduce((s, d) => s + (d.projected_commission || 0), 0);
  const totalClosed = deals.filter((d) => d.closed).length;

  return (
    <div className="space-y-6" data-testid="deal-pipeline">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">
            <EditableHeader
              label={config.page_title || "Pipeline"}
              fieldKey="page_title"
              onSave={(v) => saveConfig({ ...config, page_title: v })}
              testId="pipe-page-title-inline"
            />
          </div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            {fmtMoney(totalProjected)}
          </h1>
          <div className="mono text-xs text-muted-foreground tracking-widest mt-2 uppercase">
            {totalClosed} closed · {deals.length - totalClosed} active
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search…"
            className="hud-input !w-44"
            data-testid="deal-search"
          />
          <button onClick={add} className="hud-btn flex items-center gap-2" data-testid="deal-add">
            <Plus size={12} /> NEW DEAL
          </button>
          <button onClick={onExport} className="hud-btn flex items-center gap-2" data-testid="deal-export">
            <Download size={12} /> CSV
          </button>
          <button onClick={() => setImportOpen(true)} className="hud-btn flex items-center gap-2" data-testid="deal-import">
            <Upload size={12} /> IMPORT
          </button>
          <button onClick={() => setConfigOpen(true)} className="hud-btn flex items-center gap-2" data-testid="deal-configure">
            <Gear size={12} /> CONFIGURE
          </button>
          <button
            onClick={() => setManual((m) => !m)}
            disabled={filterActive}
            className={`hud-btn flex items-center gap-2 ${manual && !filterActive ? "hud-btn-primary" : ""}`}
            title={filterActive ? "Clear the search to reorder rows" : "Drag rows into a custom order"}
            data-testid="deal-manual-order"
          >
            <ArrowUpDown size={12} /> MANUAL ORDER
          </button>
          <button onClick={reset} className="hud-btn !text-[10px]" title="Reset column widths" data-testid="deal-reset-widths">
            RESET WIDTHS
          </button>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 -mt-2">
        <SortStatus
          sort={sort}
          manual={manualActive}
          labelFor={labelFor}
          shown={display.length}
          total={deals.length}
          onResetOrder={resetOrder}
        />
      </div>

      <LastImportBanner target="deals" onChanged={load} refreshToken={importBump} />

      {configOpen && (
        <PipelineConfigModal
          config={config}
          onClose={() => setConfigOpen(false)}
          onSave={saveConfig}
        />
      )}

      {importOpen && (
        <ImportModal
          target="deals"
          onClose={() => setImportOpen(false)}
          onImported={onImported}
        />
      )}

      {sendDeal && (
        <div
          className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-start justify-center pt-16 px-4 animate-fadein"
          onClick={() => setSendDeal(null)}
          data-testid="send-project-modal"
        >
          <div
            className="hud-frame w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "hsl(var(--popover))" }}
          >
            <div className="tread-plate h-1.5" />
            <div className="p-6 space-y-4">
              <div className="kpi-label">Send to project</div>
              <h2 className="display text-lg font-bold uppercase">
                {sendDeal.client || sendDeal.property || "Deal"}
              </h2>
              <p className="text-sm text-muted-foreground">
                Create a project row from this deal in one of your tracker tabs.
              </p>
              {trackerTabs.length === 0 ? (
                <p className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                  No tracker tabs yet — create one from the sidebar first.
                </p>
              ) : (
                <div className="space-y-2">
                  {trackerTabs.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => sendToProject(t.id)}
                      className="hud-btn w-full justify-start flex items-center gap-2"
                      data-testid={`send-to-tab-${t.id}`}
                    >
                      <FolderInput size={12} /> {t.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={() => setSendDeal(null)} className="hud-btn" data-testid="send-project-cancel">
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                <th ref={setRef(0)} style={widthStyle(0, "180px")}>
                  <EditableHeader label={L("client")} fieldKey="client" onSave={(v) => saveLabel("client", v)} testId="pipe-hdr-client" />
                  <SortCaret field="client" type="string" sort={sort} toggle={onSort} testId="sort-client" />
                  <Grip index={0} />
                </th>
                <th ref={setRef(1)} style={widthStyle(1, "200px")}>
                  <EditableHeader label={L("property")} fieldKey="property" onSave={(v) => saveLabel("property", v)} testId="pipe-hdr-property" />
                  <SortCaret field="property" type="string" sort={sort} toggle={onSort} />
                  <Grip index={1} />
                </th>
                <th ref={setRef(2)} style={widthStyle(2, "130px")}>
                  <EditableHeader label={L("deal_type")} fieldKey="deal_type" onSave={(v) => saveLabel("deal_type", v)} />
                  <SortCaret field="deal_type" type="string" sort={sort} toggle={onSort} />
                  <Grip index={2} />
                </th>
                <th ref={setRef(3)} style={widthStyle(3, "150px")}>
                  <EditableHeader label={L("stage")} fieldKey="stage" onSave={(v) => saveLabel("stage", v)} />
                  <SortCaret field="stage" type="string" sort={sort} toggle={onSort} />
                  <Grip index={3} />
                </th>
                <th ref={setRef(4)} style={widthStyle(4, "140px")}>
                  <EditableHeader label={L("source")} fieldKey="source" onSave={(v) => saveLabel("source", v)} />
                  <SortCaret field="source" type="string" sort={sort} toggle={onSort} />
                  <Grip index={4} />
                </th>
                <th ref={setRef(5)} style={widthStyle(5, "130px")}>
                  <EditableHeader label={L("expected_close")} fieldKey="expected_close" onSave={(v) => saveLabel("expected_close", v)} />
                  <SortCaret field="expected_close_date" type="date" sort={sort} toggle={onSort} />
                  <Grip index={5} />
                </th>
                <th ref={setRef(6)} style={widthStyle(6, "130px")} className="!text-right">
                  <EditableHeader label={L("price")} fieldKey="price" onSave={(v) => saveLabel("price", v)} />
                  <SortCaret field="price" type="number" sort={sort} toggle={onSort} testId="sort-price" />
                  <Grip index={6} />
                </th>
                <th ref={setRef(7)} style={widthStyle(7, "100px")} className="!text-right">
                  <EditableHeader label={L("commission_pct")} fieldKey="commission_pct" onSave={(v) => saveLabel("commission_pct", v)} />
                  <SortCaret field="commission_pct" type="number" sort={sort} toggle={onSort} />
                  <Grip index={7} />
                </th>
                <th ref={setRef(8)} style={widthStyle(8, "130px")} className="!text-right">
                  <EditableHeader label={L("projected_commission")} fieldKey="projected_commission" onSave={(v) => saveLabel("projected_commission", v)} />
                  <SortCaret field="projected_commission" type="number" sort={sort} toggle={onSort} />
                  <Grip index={8} />
                </th>
                <th ref={setRef(9)} style={widthStyle(9, "90px")} className="!text-center">
                  <EditableHeader label={L("closed")} fieldKey="closed" onSave={(v) => saveLabel("closed", v)} />
                  <SortCaret field="closed" type="bool" sort={sort} toggle={onSort} />
                  <Grip index={9} />
                </th>
                <th ref={setRef(10)} style={widthStyle(10, "130px")}>
                  <EditableHeader label={L("closed_date")} fieldKey="closed_date" onSave={(v) => saveLabel("closed_date", v)} />
                  <SortCaret field="closed_date" type="date" sort={sort} toggle={onSort} />
                  <Grip index={10} />
                </th>
                <th ref={setRef(11)} style={widthStyle(11, "130px")}>
                  <EditableHeader label={L("next_follow_up")} fieldKey="next_follow_up" onSave={(v) => saveLabel("next_follow_up", v)} />
                  <SortCaret field="next_follow_up" type="date" sort={sort} toggle={onSort} />
                  <Grip index={11} />
                </th>
                <th ref={setRef(12)} style={widthStyle(12, "110px")}>
                  <EditableHeader label={L("priority")} fieldKey="priority" onSave={(v) => saveLabel("priority", v)} />
                  <SortCaret field="priority" type="string" sort={sort} toggle={onSort} />
                  <Grip index={12} />
                </th>
                <th ref={setRef(13)} style={widthStyle(13, "130px")}>
                  <EditableHeader label={L("status")} fieldKey="status" onSave={(v) => saveLabel("status", v)} />
                  <SortCaret field="status" type="string" sort={sort} toggle={onSort} />
                  <Grip index={13} />
                </th>
                <th ref={setRef(14)} style={widthStyle(14, "220px")}>
                  <EditableHeader label={L("notes")} fieldKey="notes" onSave={(v) => saveLabel("notes", v)} />
                  <SortCaret field="notes" type="string" sort={sort} toggle={onSort} />
                  <Grip index={14} />
                </th>
                <th ref={setRef(15)} style={widthStyle(15, "72px")} /></tr>
            </thead>
            <tbody>
              {display.map((d) => (
                <tr key={d.id} data-testid={`deal-row-${d.id}`} {...(manualActive ? drag.rowProps(d.id) : {})}>
                  <td>
                    <div className="flex items-center gap-1">
                      {manualActive && (
                        <button
                          {...drag.handleProps(d.id)}
                          className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground"
                          title="Drag to reorder"
                          data-testid={`deal-drag-${d.id}`}
                          aria-label="Drag to reorder"
                        >
                          <GripVertical size={13} />
                        </button>
                      )}
                      <input
                        defaultValue={d.client}
                        onBlur={(e) =>
                          e.target.value !== d.client && update(d.id, { client: e.target.value })
                        }
                        className="hud-input !border-transparent hover:!border-border !py-1"
                        placeholder="Client"
                      />
                    </div>
                  </td>
                  <td>
                    <input
                      defaultValue={d.property}
                      onBlur={(e) =>
                        e.target.value !== d.property &&
                        update(d.id, { property: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Property"
                    />
                  </td>
                  <td>
                    <select
                      value={d.deal_type || ""}
                      onChange={(e) => update(d.id, { deal_type: e.target.value })}
                      className="hud-input !py-1 !bg-transparent"
                    >
                      <option value="">—</option>
                      {(config.deal_types || []).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={d.stage || (config.stages?.[0] || "Lead")}
                      onChange={(e) => update(d.id, { stage: e.target.value })}
                      className="hud-input !py-1 !bg-transparent"
                    >
                      {(config.stages || []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      defaultValue={d.source}
                      onBlur={(e) =>
                        e.target.value !== d.source && update(d.id, { source: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Source"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      defaultValue={d.expected_close_date || ""}
                      onBlur={(e) =>
                        update(d.id, { expected_close_date: e.target.value || null })
                      }
                      className="hud-input !py-1"
                    />
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      defaultValue={d.price || 0}
                      onBlur={(e) =>
                        update(d.id, { price: Number(e.target.value) || 0 })
                      }
                      className="hud-input !py-1 !text-right num"
                    />
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="0.1"
                      defaultValue={d.commission_pct || 0}
                      onBlur={(e) =>
                        update(d.id, { commission_pct: Number(e.target.value) || 0 })
                      }
                      className="hud-input !py-1 !text-right num"
                    />
                  </td>
                  <td className="num font-semibold text-[hsl(var(--oxblood))]">
                    {fmtMoney(d.projected_commission)}
                  </td>
                  <td className="!text-center">
                    <Check
                      checked={d.closed}
                      onChange={(v) => update(d.id, { closed: v })}
                      testid={`deal-closed-${d.id}`}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      defaultValue={d.closed_date || ""}
                      onBlur={(e) =>
                        update(d.id, { closed_date: e.target.value || null })
                      }
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      defaultValue={d.next_follow_up || ""}
                      onBlur={(e) =>
                        update(d.id, { next_follow_up: e.target.value || null })
                      }
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <select
                      value={d.priority || "Medium"}
                      onChange={(e) => update(d.id, { priority: e.target.value })}
                      className="hud-input !py-1 !bg-transparent"
                    >
                      {(config.priorities || []).map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span
                      className={`hud-chip ${d.closed ? "hud-chip-on" : ""}`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td>
                    <input
                      defaultValue={d.notes}
                      onBlur={(e) =>
                        e.target.value !== d.notes &&
                        update(d.id, { notes: e.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1"
                      placeholder="Notes"
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSendDeal(d)}
                        className="text-muted-foreground hover:text-[hsl(var(--oxblood))]"
                        title="Send to a project tab"
                        data-testid={`deal-to-project-${d.id}`}
                      >
                        <FolderInput size={13} />
                      </button>
                      <button
                        onClick={() => remove(d.id)}
                        className="text-muted-foreground hover:text-[hsl(var(--signal))]"
                        data-testid={`deal-delete-${d.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={16} className="text-center text-muted-foreground py-10">
                    No deals yet. Click <span className="text-[hsl(var(--oxblood))]">NEW DEAL</span> to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
